/**
 * MeetingsSection — project-scoped Meetings (meeting minutes) under Reference > Meetings.
 *
 * Outline-first action model (no buttons outside action menus):
 *   - Every meeting TYPE is always shown as a category branch (OAC, Design Progress, …), even with
 *     zero meetings, so you can start one. "New meeting" lives on the category's ⋯ menu and adopts
 *     that category as the type (the create modal assumes it — no type dropdown).
 *   - Per-meeting actions live on the meeting row's ⋯ (list) and, when zoomed into a meeting, on the
 *     ⋯ next to the title in ZoomTitle (which dispatches the meeting-* events this component handles).
 *
 * States: 'home' / 'referenceZoom' (root node), 'rootZoom' (all categories), 'detailZoom' (one
 * meeting's minutes = its threads, grouped by category). See docs/MEETING_MINUTES.md.
 */

import { useState, useEffect, useReducer, useRef } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { showPill } from '../outline/utils.js'
import { getProjectThreads } from '../services/threads.js'
import { getContactNamesByIds } from '../services/contacts.js'
import {
  getProjectMeetings, meetingDisplay, MEETING_TYPES,
  getMeetingThreadIds, addThreadToMeeting, deleteMeeting,
  previewMeetingMinutes, getProjectMeetingNotesDrafts, postMeetingMinutes,
  refreshMeetingAgenda, addThreadsToMeeting, addMeetingAttendees,
} from '../services/meetings.js'
import { openThreadPicker } from './OpenThreadPicker.jsx'
import ThreadCategoryList from './ThreadCategoryList.jsx'
import CreateThreadModal from './CreateThreadModal.jsx'
import CreateMeetingModal from './CreateMeetingModal.jsx'
import MeetingNotesImportModal from './MeetingNotesImportModal.jsx'
import PostMinutesModal from './PostMinutesModal.jsx'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'

/* ── Visibility ─────────────────────────────────────────────────────────── */

function calcMeetingsVis() {
  if (!wf.zoomProjectId) return 'hidden'
  const clean = !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomCat && !wf.zoomItemId &&
                !wf.zoomConceptId && !wf.searchQuery
  if (wf.zoomSection && /^sec:meeting:[^:]+:details$/.test(wf.zoomSection) && clean) return 'detailsZoom'
  if (wf.zoomSection && /^sec:meeting:[^:]+:threads$/.test(wf.zoomSection) && clean) return 'mthreadsZoom'
  if (wf.zoomSection && wf.zoomSection.indexOf('meeting-detail:') === 0 && clean) return 'detailZoom'
  if (wf.zoomSection && wf.zoomSection.indexOf('meeting-cat:') === 0 && clean) return 'catZoom'
  if (wf.zoomSection) return 'hidden'
  if (wf.zoomRoot === 'meetings' && clean) return 'rootZoom'
  if (wf.zoomRoot === 'reference' && clean) return 'referenceZoom'
  if (!wf.zoomRoot && clean) return 'home'
  return 'hidden'
}

/* ── Filler row (loading / empty, standard outline grid) ────────────────── */

function FillerRow({ text, italic }) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: '#7a9aaa', ...(italic ? { fontStyle: 'italic' } : {}) }}>{text}</span>
        </div>
      </div>
    </div>
  )
}

/* ── Meeting detail rows + the two child branches (Details / Threads) ───────
 * A meeting node's children are the same everywhere (inline-expanded row + zoomed-in view): a
 * "Meeting Details" branch (leaf detail rows) and a "Threads" branch (the linked threads). Both
 * branches are zoomable (sec:meeting:<id>:details / :threads) by click and keyboard. */

function fmtDateTime(iso) {
  if (!iso) return ''
  const dt = new Date(iso)
  if (isNaN(dt)) return ''
  return dt.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles', timeZoneName: 'short' })
}

/* A leaf "Label: value" detail row (no children, not zoomable). */
function DetailRow({ label, value, meetingId }) {
  if (!value) return null
  return (
    <div className="wf-node wf-node--item" data-key={'mtg-detail:' + meetingId + ':' + label.toLowerCase()}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text"><span style={{ color: '#7a9aaa' }}>{label}: </span>{value}</span>
        </div>
      </div>
    </div>
  )
}

// Module cache: attendee Contact id → resolved name (the PDF's source). Shared across detail rows.
const attendeeNameCache = {}

function MeetingDetailRows({ meeting }) {
  const [, force] = useReducer(v => v + 1, 0)
  const d = meetingDisplay(meeting)
  const f = meeting.fields || {}
  const ids = d.attendeeIds || []
  // Resolve ALL attendees (not just team — the old {Name (from Team Attendees)} lookup hid clients).
  useEffect(() => {
    const missing = ids.filter(id => attendeeNameCache[id] === undefined)
    if (!missing.length) return
    missing.forEach(id => { attendeeNameCache[id] = null })   // in-flight marker
    getContactNamesByIds(missing).then(map => {
      missing.forEach(id => { attendeeNameCache[id] = map[id] || '' })
      force()
    })
  }, [ids.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  const resolved = ids.map(id => attendeeNameCache[id]).filter(Boolean)
  // Show resolved attendee names once available; fall back to the team-name lookup while loading.
  const attendeeLabel = resolved.length
    ? resolved.join(', ')
    : ((f['Name (from Team Attendees)'] || []).join(', ') || (ids.length ? ids.length + ' attendee' + (ids.length !== 1 ? 's' : '') : ''))
  return (
    <>
      <DetailRow meetingId={meeting.id} label="Date" value={fmtDateTime(d.date) || d.dateStr} />
      <DetailRow meetingId={meeting.id} label="Status" value={d.posted ? 'Posted · Locked' : d.statusMsg} />
      <DetailRow meetingId={meeting.id} label="Location" value={d.location} />
      <DetailRow meetingId={meeting.id} label="Phase" value={d.phase} />
      <DetailRow meetingId={meeting.id} label="Attendees" value={attendeeLabel} />
      <DetailRow meetingId={meeting.id} label="Consultants" value={(d.consultants || []).join(', ')} />
      {d.postedPdfUrl && (
        <div className="wf-node wf-node--item" data-key={'mtg-detail:' + meeting.id + ':minutes'}>
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-bullet wf-bullet--item"></span>
            <div className="wf-text-wrap">
              <a href={d.postedPdfUrl} target="_blank" rel="noreferrer" className="wf-node-text" style={{ color: '#07272D', fontWeight: 600 }}>Open issued minutes ↗</a>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/* Expand/collapse a single branch node (Meeting Details is leaf rows — nothing deeper to recurse). */
function setBranch(key, expand) {
  wf.collapsed[key] = !expand
  saveViewState()
  window.dispatchEvent(new Event('outline-render'))
}

/* Expand/collapse the Threads branch AND every level beneath it — category groups, thread rows, and
   each thread's activity log. The engine's expandCollapseDescendants can't reach React children that
   aren't rendered yet (collapsed nodes), so we set the deterministic keys directly: the branch,
   mtgcat:<mid>:<Type> categories, thread:<id> rows, and thread-acts:<id> logs. */
function setThreadsBranchAll(meetingId, threads, expand) {
  const c = !expand
  wf.collapsed['sec:meeting:' + meetingId + ':threads'] = c
  const types = new Set()
  ;(threads || []).forEach(t => types.add((t.fields && t.fields['Type']) || 'Misc. Items'))
  types.forEach(type => { wf.collapsed['mtgcat:' + meetingId + ':' + type] = c })
  ;(threads || []).forEach(t => { wf.collapsed['thread:' + t.id] = c; wf.collapsed['thread-acts:' + t.id] = c })
  saveViewState()
  window.dispatchEvent(new Event('outline-render'))
}

function MeetingDetailsBranch({ meeting }) {
  const key = 'sec:meeting:' + meeting.id + ':details'
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Focus on meeting details" data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'meetings' }) }}></button>
        <div className="wf-text-wrap"><span className="wf-node-text wf-node-text--section">Meeting Details</span></div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => setBranch(key, true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setBranch(key, false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children"><MeetingDetailRows meeting={meeting} /></div>}
    </div>
  )
}

function MeetingThreadsContent({ threads, projectId, projectName, meetingId }) {
  if (!threads) return <FillerRow text="Loading items…" />
  if (threads.length === 0) return <FillerRow text="No threads on this meeting yet. Use the ⋯ menu by the title to add one." italic />
  return <ThreadCategoryList threads={threads} projectId={projectId} projectName={projectName} scopeKey={'mtgcat:' + meetingId} meetingId={meetingId} />
}

function MeetingThreadsBranch({ meeting, threads, projectId, projectName }) {
  const key = 'sec:meeting:' + meeting.id + ':threads'
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const n = (meeting.fields['Threads'] || []).length
  function toggle(e) { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Focus on threads" data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'meetings' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section" style={{ flex: 1, minWidth: 0 }}>Threads</span>
          {n > 0 && <span className="wf-prog" style={{ color: '#7a9aaa' }}>{n}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => setThreadsBranchAll(meeting.id, threads, true)}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => setThreadsBranchAll(meeting.id, threads, false)}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children"><MeetingThreadsContent threads={threads} projectId={projectId} projectName={projectName} meetingId={meeting.id} /></div>}
    </div>
  )
}

/* ── Agenda by who is involved (api/_meetingAgenda.js, Matt 2026-10-19) ──────────────────────
 * Until the meeting is held, opening it refreshes its agenda (open threads that involve someone
 * attending) and names any party whose open threads are missing because they aren't invited,
 * with one click to add them. Refreshed once per meeting per page load. */
const _agendaSeen = new Map()   // meetingId → last agenda response
function applyAgenda(meeting, a) {
  if (!a) return
  _agendaSeen.set(meeting.id, a)
  if (Array.isArray(a.onAgenda)) meeting.fields['Threads'] = a.onAgenda
  window.dispatchEvent(new Event('outline-render'))
}
export async function addOpenThreadsFlow(meeting) {
  const on = new Set(getMeetingThreadIds(meeting))
  const threads = (Array.isArray(ctx.threadCache) ? ctx.threadCache : []).filter(t => {
    const f = t.fields || {}
    return !on.has(t.id) && !(f['Closed'] === true || String(f['Status'] || '').includes('Closed'))
  }).map(t => ({ id: t.id, title: t.fields['Title'] || '(untitled)', type: t.fields['Type'] || '', court: [].concat(t.fields['Ball In Court Name'] || t.fields['Name (from Ball In Court)'] || [])[0] || null }))
  const ids = await openThreadPicker({ threads })
  if (!ids || !ids.length) return
  try { applyAgenda(meeting, await addThreadsToMeeting(meeting.id, ids)) }
  catch (e) { alertDialog({ title: 'Could not add them', message: e.message || String(e) }) }
}
function MeetingAgendaBar({ meeting }) {
  const d = meetingDisplay(meeting)
  const [a, setA] = useState(() => _agendaSeen.get(meeting.id) || null)
  const [busy, setBusy] = useState(null)
  useEffect(() => {
    if (d.posted || _agendaSeen.has(meeting.id)) return
    _agendaSeen.set(meeting.id, null)
    refreshMeetingAgenda(meeting.id).then(r => { applyAgenda(meeting, r); setA(r) }).catch(() => _agendaSeen.delete(meeting.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.id])
  if (d.posted || !a || a.held || !(a.missing || []).length) return null
  async function addParty(g) {
    setBusy(g.party)
    try { const r = await addMeetingAttendees(meeting.id, g.contactIds); applyAgenda(meeting, r); setA(r) }
    catch (e) { alertDialog({ title: 'Could not add them', message: e.message || String(e) }) }
    setBusy(null)
  }
  return (
    <div style={{ margin: '2px 0 6px var(--wf-indent, 49px)', display: 'grid', gap: 4 }}>
      {a.missing.map(g => (
        <div key={g.party} style={{ fontSize: 12.5, color: '#b7791f', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span title={g.threads.map(t => t.title).join(' · ')}>
            {g.threads.length} open thread{g.threads.length === 1 ? '' : 's'} involve{g.threads.length === 1 ? 's' : ''} {g.name}, who isn’t on this meeting.
          </span>
          <button disabled={busy === g.party} onClick={() => addParty(g)}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--aj-accent-blue, #1D9E75)', cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>
            {busy === g.party ? 'Adding…' : 'Add ' + g.name}
          </button>
        </div>
      ))}
    </div>
  )
}

/* The meeting node's children, shared by the inline-expanded row and the zoomed-in detail view. */
function MeetingChildren({ meeting, threads, projectId, projectName }) {
  return (
    <>
      <MeetingAgendaBar meeting={meeting} />
      <MeetingDetailsBranch meeting={meeting} />
      <MeetingThreadsBranch meeting={meeting} threads={threads} projectId={projectId} projectName={projectName} />
    </>
  )
}

/* ── A meeting row in the grouped list ──────────────────────────────────── */

function MeetingRow({ meeting, projectId, projectName, onOpen, onEdit, onPost, onDelete, onPreview, onAddThread, onImport }) {
  const d = meetingDisplay(meeting)
  const menu = useActionMenu()
  const posted = d.posted
  const statusColor = posted ? '#1D9E75' : d.stage === 0 ? '#7a9aaa' : '#b8860b'
  const key = 'meeting:' + meeting.id
  const collapsed = wf.collapsed[key] !== false
  // Expand a meeting row to reveal its minutes (linked threads, grouped by category) inline —
  // the bullet still zooms into the full meeting view.
  const threads = ctx.threadCache
  const linkedIds = getMeetingThreadIds(meeting)
  const linked = Array.isArray(threads) ? threads.filter(t => linkedIds.includes(t.id)) : null
  const draft = (ctx.meetingNotesDraftCache || {})[meeting.id] || null
  function toggle(e) {
    e.stopPropagation()
    wf.collapsed[key] = !collapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  return (
    <div className={`wf-node wf-node--item${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title="Open minutes" data-kbzoom="1" onClick={e => { e.stopPropagation(); onOpen(meeting.id) }}></button>
        <div className="wf-text-wrap">
          {/* flex:1 on the date pushes the status/items to the right edge (same as the category
              counts). wf-prog keeps their font size consistent with the rest of the outline. */}
          <span className="wf-node-text" style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onOpen(meeting.id)}>
            {d.dateStr || 'Undated meeting'}
          </span>
          <span className="wf-prog" style={{ color: statusColor }}>{posted ? 'Posted · Locked' : d.statusMsg}</span>
          {d.threadIds.length > 0 && (
            <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{d.threadIds.length} item{d.threadIds.length !== 1 ? 's' : ''}</span>
          )}
          {!posted && draft && (
            <span className="wf-prog" style={{ color: draft.status === 'ready_for_review' ? '#b8860b' : '#7a9aaa', marginLeft: 8 }}>
              Notes: {draft.status === 'ready_for_review' ? 'ready for review' : 'draft'}
            </span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      {/* Mirrors the zoomed-in meeting menu (ZoomTitle) exactly — one menu for both views. */}
      <ActionMenu menu={menu}>
        {posted ? (
          <>
            {d.postedPdfUrl && <ActionMenuItem onClick={() => window.open(d.postedPdfUrl, '_blank')}>Open issued minutes</ActionMenuItem>}
            <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('meeting-repost', { detail: { meetingId: meeting.id } }))}>Repost revised minutes</ActionMenuItem>
            {/* Posted before its notes import was committed: finishing it reposts the minutes with the items. */}
            {draft && <ActionMenuItem onClick={() => onImport(meeting.id)}>Finish Zoom notes import…</ActionMenuItem>}
            <ActionMenuItem onClick={() => onEdit(meeting.id)}>Edit meeting</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem onClick={() => onDelete(meeting)} danger>Delete meeting</ActionMenuItem>
          </>
        ) : (
          <>
            <ActionMenuItem onClick={() => onAddThread(meeting.id)}>Add thread</ActionMenuItem>
            <ActionMenuItem onClick={() => addOpenThreadsFlow(meeting)}>Add an open thread…</ActionMenuItem>
            {draft && draft.status === 'ready_for_review'
              ? <ActionMenuItem onClick={() => onImport(meeting.id)}>Review Zoom notes</ActionMenuItem>
              : draft
              ? <ActionMenuItem onClick={() => onImport(meeting.id)}>Continue Zoom notes import…</ActionMenuItem>
              : d.notesImported
              ? <div className="wf-menu-item" style={{ opacity: 0.5, cursor: 'default' }} title="Zoom notes have already been imported for this meeting">Zoom notes imported ✓</div>
              : <ActionMenuItem onClick={() => onImport(meeting.id)}>Import Zoom notes</ActionMenuItem>}
            <ActionMenuItem onClick={() => onEdit(meeting.id)}>Edit meeting</ActionMenuItem>
            <ActionMenuItem onClick={() => onPreview(meeting.id)}>Preview PDF</ActionMenuItem>
            <ActionMenuItem onClick={() => onPost(meeting)}>Post</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem onClick={() => onDelete(meeting)} danger>Delete meeting</ActionMenuItem>
          </>
        )}
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          <MeetingChildren meeting={meeting} threads={linked} projectId={projectId} projectName={projectName} />
        </div>
      )}
    </div>
  )
}

/* ── Type category branch — always present (placeholder when empty); ⋯ creates a meeting of this type ── */

function MeetingTypeGroup({ type, meetings, handlers }) {
  const key = 'mtgtype:' + type
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  function toggle(e) {
    e.stopPropagation()
    wf.collapsed[key] = !collapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  function setAll(expand) {
    wf.collapsed[key] = !expand
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--section"
          title={'Focus on ' + type + ' meetings'}
          data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection('meeting-cat:' + type, { root: 'meetings' }) }}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{type}</span>
          <span className="wf-prog" style={{ marginLeft: 8, color: '#7a9aaa' }}>{meetings.length}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => handlers.onNewMeeting(type)}>New {type} meeting</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => setAll(true)}>Expand</ActionMenuItem>
        <ActionMenuItem onClick={() => setAll(false)}>Collapse</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {meetings.length === 0
            ? <FillerRow text="No meetings yet." italic />
            : meetings.map(m => <MeetingRow key={m.id} meeting={m} {...handlers} />)}
        </div>
      )}
    </div>
  )
}

/* ── Meetings list — every canonical type always shown (placeholders included) ── */

function MeetingsListContent({ meetings, handlers }) {
  if (!meetings) return <FillerRow text="Loading…" />
  const byType = {}
  meetings.forEach(m => { const t = (m.fields && m.fields['Type']) || 'Other'; (byType[t] = byType[t] || []).push(m) })
  const extra = Object.keys(byType).filter(t => !MEETING_TYPES.includes(t))
  return [...MEETING_TYPES, ...extra].map(type => (
    <MeetingTypeGroup key={type} type={type} meetings={byType[type] || []} handlers={handlers} />
  ))
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function MeetingsSection() {
  const [, forceUpdate]   = useReducer(v => v + 1, 0)
  const [vis, setVis]     = useState(calcMeetingsVis)
  const [recordId, setRecordId] = useState(() => wf.zoomProjectId || null)
  const [createType, setCreateType] = useState(null) // type string → CreateMeetingModal open (preset)
  const [editId, setEditId]   = useState(null)
  const [addThreadFor, setAddThreadFor] = useState(null)
  const [importFor, setImportFor] = useState(null)   // meetingId whose Zoom-notes import modal is open
  const [postFor, setPostFor] = useState(null)       // meetingId whose Post & email modal is open
  const meetingsLoaded = useRef(false)
  const threadsLoaded  = useRef(false)
  const draftsLoaded   = useRef(false)
  const actionsRef = useRef({})

  const projectName = ctx.state?.milestones?.[0]?.fields?.['Project Name'] || ''
  const getToken = (typeof window !== 'undefined' && window.__clerkGetToken) || null

  function loadMeetings(projId) {
    if (!projId || meetingsLoaded.current) return
    meetingsLoaded.current = true
    if (ctx.meetingCache !== undefined) return
    ctx.meetingCache = null
    getProjectMeetings(projId)
      .then(records => { ctx.meetingCache = records; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.meetingCache = []; window.dispatchEvent(new Event('outline-render')) })
  }
  function loadThreads(projId) {
    if (!projId || threadsLoaded.current) return
    threadsLoaded.current = true
    if (ctx.threadCache !== undefined) return
    ctx.threadCache = null
    getProjectThreads(projId)
      .then(records => { ctx.threadCache = records; window.dispatchEvent(new Event('outline-render')) })
      .catch(() => { ctx.threadCache = []; window.dispatchEvent(new Event('outline-render')) })
  }
  function loadDrafts(projId) {
    if (!projId || draftsLoaded.current) return
    draftsLoaded.current = true
    if (ctx.meetingNotesDraftCache !== undefined) return
    ctx.meetingNotesDraftCache = null
    getProjectMeetingNotesDrafts(projId, { getToken })
      .then(res => {
        const byMeeting = {}
        ;(res && res.drafts || []).forEach(d => { byMeeting[d.meeting_id] = d })
        ctx.meetingNotesDraftCache = byMeeting
        window.dispatchEvent(new Event('outline-render'))
      })
      .catch(() => { ctx.meetingNotesDraftCache = {}; window.dispatchEvent(new Event('outline-render')) })
  }
  function reloadMeetings() {
    if (!recordId) return
    ctx.meetingCache = undefined
    meetingsLoaded.current = false
    loadMeetings(recordId)
  }
  function reloadDrafts() {
    if (!recordId) return
    ctx.meetingNotesDraftCache = undefined
    draftsLoaded.current = false
    loadDrafts(recordId)
  }

  /* ── Action handlers (invoked from row menus + meeting-* events from ZoomTitle) ── */
  function openMeeting(id) { ctx.zoomToSection('meeting-detail:' + id, { root: 'meetings' }) }
  function newMeeting(type) { setCreateType(type) }

  async function deleteMtg(meeting) {
    const d = meetingDisplay(meeting)
    if (!(await confirmDialog({
      title: 'Delete meeting?',
      message: `Delete the ${d.type} meeting on ${d.dateStr}? Its threads are not deleted — they stay on the project and any other meetings.`,
      confirmLabel: 'Delete meeting', danger: true,
    }))) return
    try { await deleteMeeting(meeting.id) } catch (_) { /* surfaced below */ }
    if (wf.zoomSection === 'meeting-detail:' + meeting.id) ctx.zoomToRoot && ctx.zoomToRoot('meetings')
    reloadMeetings()
  }
  // Open the Post & email confirm modal — it shows recipients + email preview and lets us add
  // FYI CCs before issuing. The actual post happens inside the modal.
  function postMtg(meeting) { setPostFor(meeting && meeting.id) }
  async function previewMtg(meetingId) {
    // Rendering the PDF takes a couple seconds, so give immediate feedback: open the tab inside the
    // click gesture (no popup-block) with a placeholder, plus an in-app pill, then point the tab at
    // the rendered PDF when it's ready.
    const tab = window.open('', '_blank')
    if (tab) {
      try {
        tab.document.write('<!doctype html><meta charset="utf-8"><title>Generating preview…</title>' +
          '<body style="margin:0;font-family:\'DM Sans\',Arial,sans-serif;color:#07272D;background:#f4f7f8;display:flex;align-items:center;justify-content:center;height:100vh;">' +
          '<div style="text-align:center"><div style="font-weight:700;letter-spacing:.18em;text-transform:uppercase;font-size:12px;margin-bottom:10px">Alder James</div>' +
          '<div style="font-size:14px;color:#5a6b78">Generating meeting minutes preview…</div></div></body>')
        tab.document.close()
      } catch (_) { /* ignore */ }
    }
    showPill('Generating minutes preview…')
    const res = await previewMeetingMinutes(meetingId, { getToken })
    if (res && res.pdfUrl) {
      if (tab) tab.location = res.pdfUrl
      else window.open(res.pdfUrl, '_blank')
    } else {
      if (tab) { try { tab.close() } catch (_) {} }
      await alertDialog({ title: 'Preview unavailable', message: (res && res.error) || 'Could not render the preview.' })
    }
  }
  // Keep the ref pointed at the latest closures for the (mount-only) event listeners.
  // A revision after posting (docs/MEETING_MINUTES.md "Posting on commit"): correct the thread notes,
  // then re-issue. The new PDF replaces the old one, the Client Hub shows it (it reads the meeting's
  // posted PDF live), the client's revision date is kept, and nobody is emailed again.
  async function repostMtg(meetingId) {
    if (!(await confirmDialog({ title: 'Repost revised minutes?', message: 'Re-issues the minutes from the threads as they stand now. The Client Hub shows the new copy; nobody is emailed. Let the client know personally.', confirmLabel: 'Repost' }))) return
    showPill('Reposting the minutes…')
    const res = await postMeetingMinutes(meetingId, { send: false, repost: true, getToken, postingMemberId: (ctx.currentMember && ctx.currentMember.id) || wf.currentMemberId || null })
    if (res && res.error) { await alertDialog({ title: 'Repost failed', message: res.error }); return }
    reloadMeetings()
    await alertDialog({ title: 'Minutes reposted', message: 'The revised minutes are posted and the Client Hub shows them.' })
  }
  actionsRef.current = { previewMtg, postMtg, deleteMtg, repostMtg }

  // Reset + reload on project change
  useEffect(() => {
    if (!recordId) return
    ctx.meetingCache = undefined
    meetingsLoaded.current = false
    threadsLoaded.current = false
    draftsLoaded.current = false
    loadMeetings(recordId)
  }, [recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load whenever on-screen (covers page refresh into a meeting)
  useEffect(() => {
    if (vis === 'hidden' || !recordId) return
    loadMeetings(recordId)
    loadThreads(recordId)   // threads power both the detail view and inline meeting-row expansion
    loadDrafts(recordId)    // pending Zoom-notes imports, for the row badge + ⋯ menu state
  }, [vis, recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  // outline-render subscription + meeting-action events (dispatched by ZoomTitle's ⋯ menu)
  useEffect(() => {
    function onRender() {
      const newRecordId = wf.zoomProjectId || null
      setRecordId(prev => (prev !== newRecordId ? newRecordId : prev))
      setVis(calcMeetingsVis())
      forceUpdate()
    }
    const findMtg = id => (ctx.meetingCache || []).find(m => m.id === id)
    const evAddThread = e => setAddThreadFor(e.detail?.meetingId)
    const evEdit      = e => setEditId(e.detail?.meetingId)
    const evPreview   = e => actionsRef.current.previewMtg(e.detail?.meetingId)
    const evPost      = e => { const m = findMtg(e.detail?.meetingId); if (m) actionsRef.current.postMtg(m) }
    const evDelete    = e => { const m = findMtg(e.detail?.meetingId); if (m) actionsRef.current.deleteMtg(m) }
    const evImport    = e => setImportFor(e.detail?.meetingId)
    const evRepost    = e => actionsRef.current.repostMtg(e.detail?.meetingId)
    window.addEventListener('meeting-repost', evRepost)
    const evAddOpen   = e => { const m = findMtg(e.detail?.meetingId); if (m) addOpenThreadsFlow(m) }
    window.addEventListener('meeting-add-open-thread', evAddOpen)
    window.addEventListener('outline-render', onRender)
    window.addEventListener('meeting-add-thread', evAddThread)
    window.addEventListener('open-edit-meeting', evEdit)
    window.addEventListener('meeting-preview', evPreview)
    window.addEventListener('meeting-post', evPost)
    window.addEventListener('meeting-delete', evDelete)
    window.addEventListener('open-meeting-import', evImport)
    return () => {
      window.removeEventListener('outline-render', onRender)
      window.removeEventListener('meeting-add-thread', evAddThread)
      window.removeEventListener('meeting-add-open-thread', evAddOpen)
      window.removeEventListener('open-edit-meeting', evEdit)
      window.removeEventListener('meeting-preview', evPreview)
      window.removeEventListener('meeting-post', evPost)
      window.removeEventListener('meeting-delete', evDelete)
      window.removeEventListener('open-meeting-import', evImport)
      window.removeEventListener('meeting-repost', evRepost)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (vis === 'hidden') return null

  const meetings = ctx.meetingCache
  const handlers = { onOpen: openMeeting, onEdit: setEditId, onPost: postMtg, onDelete: deleteMtg, onPreview: previewMtg, onAddThread: setAddThreadFor, onImport: setImportFor, onNewMeeting: newMeeting, projectId: recordId, projectName }

  // Shared modals (create / edit / details / add-thread)
  const modals = (
    <>
      {createType && (
        <CreateMeetingModal
          projectId={recordId}
          presetType={createType}
          onClose={() => setCreateType(null)}
          onSaved={() => { setCreateType(null); reloadMeetings() }}
        />
      )}
      {editId && (
        <CreateMeetingModal
          projectId={recordId}
          meetingId={editId}
          onClose={() => setEditId(null)}
          onSaved={() => { setEditId(null); reloadMeetings() }}
        />
      )}
      {importFor && (
        <MeetingNotesImportModal
          meetingId={importFor}
          projectName={projectName}
          onClose={() => setImportFor(null)}
          onCommitted={() => { ctx.threadCache = undefined; threadsLoaded.current = false; loadThreads(recordId); reloadMeetings(); reloadDrafts() }}
        />
      )}
      {postFor && (
        <PostMinutesModal
          meetingId={postFor}
          projectId={recordId}
          projectName={projectName}
          onClose={() => setPostFor(null)}
          onPosted={() => reloadMeetings()}
        />
      )}
      {addThreadFor && (
        <CreateThreadModal
          projectId={recordId}
          onClose={() => setAddThreadFor(null)}
          onCreated={async (rec) => {
            const mid = addThreadFor
            setAddThreadFor(null)
            try {
              const m = (ctx.meetingCache || []).find(x => x.id === mid)
              if (rec && rec.id && m) await addThreadToMeeting(mid, rec.id, getMeetingThreadIds(m))
            } catch (_) { /* non-fatal */ }
            ctx.threadCache = undefined; threadsLoaded.current = false; loadThreads(recordId)
            reloadMeetings()
          }}
        />
      )}
    </>
  )

  // Resolve a meeting + its linked thread records (shared by the detail + sub-section zooms).
  function resolveMeetingThreads(meetingId) {
    const meeting = Array.isArray(meetings) ? meetings.find(x => x.id === meetingId) : null
    const linkedIds = meeting ? getMeetingThreadIds(meeting) : []
    const linked = Array.isArray(ctx.threadCache) ? ctx.threadCache.filter(t => linkedIds.includes(t.id)) : null
    return { meeting, linked }
  }

  /* ── detailZoom — one meeting: Meeting Details + Threads branches (actions on the title ⋯) ── */
  if (vis === 'detailZoom') {
    const { meeting, linked } = resolveMeetingThreads(wf.zoomSection.slice('meeting-detail:'.length))
    return (
      <div className="wf-list">
        {!meetings && <FillerRow text="Loading…" />}
        {Array.isArray(meetings) && !meeting && <FillerRow text="Meeting not found." italic />}
        {meeting && <MeetingChildren meeting={meeting} threads={linked} projectId={recordId} projectName={projectName} />}
        {modals}
      </div>
    )
  }

  /* ── detailsZoom — the Meeting Details branch, children only (ZoomTitle shows "Meeting Details") ── */
  if (vis === 'detailsZoom') {
    const { meeting } = resolveMeetingThreads(wf.zoomSection.split(':')[2])
    return (
      <div className="wf-list">
        {!meetings && <FillerRow text="Loading…" />}
        {Array.isArray(meetings) && !meeting && <FillerRow text="Meeting not found." italic />}
        {meeting && <MeetingDetailRows meeting={meeting} />}
        {modals}
      </div>
    )
  }

  /* ── mthreadsZoom — the Threads branch, children only (ZoomTitle shows "Threads") ── */
  if (vis === 'mthreadsZoom') {
    const { meeting, linked } = resolveMeetingThreads(wf.zoomSection.split(':')[2])
    return (
      <div className="wf-list">
        {!meetings && <FillerRow text="Loading…" />}
        {Array.isArray(meetings) && !meeting && <FillerRow text="Meeting not found." italic />}
        {meeting && <MeetingThreadsContent threads={linked} projectId={recordId} projectName={projectName} meetingId={meeting.id} />}
        {modals}
      </div>
    )
  }

  /* ── catZoom — one meeting type's meetings (children only; ZoomTitle shows the type) ── */
  if (vis === 'catZoom') {
    const type = wf.zoomSection.slice('meeting-cat:'.length)
    const list = Array.isArray(meetings)
      ? meetings.filter(m => ((m.fields && m.fields['Type']) || 'Other') === type)
      : null
    return (
      <div className="wf-list">
        {!list && <FillerRow text="Loading…" />}
        {list && list.length === 0 && <FillerRow text="No meetings yet. Use the ⋯ menu to add one." italic />}
        {list && list.map(m => <MeetingRow key={m.id} meeting={m} {...handlers} />)}
        {modals}
      </div>
    )
  }

  /* ── rootZoom — all category branches (no toolbar; New meeting is on each category's ⋯) ── */
  if (vis === 'rootZoom') {
    return (
      <div className="wf-list">
        <MeetingsListContent meetings={meetings} handlers={handlers} />
        {modals}
      </div>
    )
  }

  /* ── home / referenceZoom — Reference-child root node ─────────────────── */
  if (vis === 'home' && wf.collapsed['root:reference'] !== false) return null

  const rootCollapsed = wf.collapsed['root:meetings'] !== false
  const count = Array.isArray(meetings) ? meetings.length : null
  const rootNode = (
    <div className={`wf-node wf-node--root wf-node--root-meetings${rootCollapsed ? ' wf-node--collapsed' : ''}`} data-key="root:meetings">
      <div className="wf-row">
        <button
          className="wf-triangle"
          onClick={e => {
            e.stopPropagation()
            const nc = !rootCollapsed
            wf.collapsed['root:meetings'] = nc
            saveViewState()
            if (!nc) loadMeetings(recordId)
            window.dispatchEvent(new Event('outline-render'))
          }}
        >
          <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--root" title="Focus on Meetings" data-fn="zoomTo" data-level="root" data-rootname="meetings"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--root">Meetings</span>
          {count != null && <span className="wf-prog" style={{ marginLeft: 8, color: '#7a9aaa' }}>{count}</span>}
        </div>
        <button className="wf-menu-btn" data-fn="openMenu" data-type="root" data-key="root:meetings" data-rootname="meetings">&#8943;</button>
      </div>
      {!rootCollapsed && <div className="wf-children"><MeetingsListContent meetings={meetings} handlers={handlers} /></div>}
    </div>
  )

  return (
    <div className="wf-list">
      {vis === 'referenceZoom' ? rootNode : <div className="wf-ref-child">{rootNode}</div>}
      {modals}
    </div>
  )
}
